import React from 'react';
import { motion } from 'framer-motion';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rectangular',
  width,
  height,
  animation = 'wave',
}) => {
  const baseClasses = 'bg-white/5 overflow-hidden';

  const variantClasses = {
    text: 'rounded-md h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-xl',
  };

  const animationVariants = {
    wave: {
      backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
      backgroundSize: '200% 100%',
    },
    pulse: {},
    none: {},
  };

  const style: React.CSSProperties = {
    width: width || '100%',
    height: height || (variant === 'text' ? '1rem' : '100%'),
    ...(animation === 'wave' ? animationVariants.wave : {}),
  };

  return (
    <motion.div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
      animate={
        animation === 'wave'
          ? {
              backgroundPosition: ['200% 0', '-200% 0'],
            }
          : animation === 'pulse'
          ? {
              opacity: [0.5, 1, 0.5],
            }
          : {}
      }
      transition={
        animation === 'wave'
          ? {
              duration: 1.5,
              repeat: Infinity,
              ease: 'linear',
            }
          : animation === 'pulse'
          ? {
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }
          : {}
      }
    />
  );
};

export default Skeleton;

export const EventCardSkeleton: React.FC = () => (
  <div className="bg-theme-cards rounded-2xl overflow-hidden shadow-card border border-theme-cards p-4 space-y-4">
    <Skeleton height={200} className="rounded-xl" />
    <div className="space-y-3">
      <Skeleton width="70%" height={24} />
      <Skeleton width="100%" height={16} />
      <Skeleton width="90%" height={16} />
      <div className="flex gap-2 mt-4">
        <Skeleton width={80} height={32} className="rounded-full" />
        <Skeleton width={100} height={32} className="rounded-full" />
      </div>
    </div>
  </div>
);

export const ProfileCardSkeleton: React.FC = () => (
  <div className="bg-theme-cards rounded-2xl overflow-hidden shadow-card border border-theme-cards p-6 space-y-4">
    <div className="flex items-center gap-4">
      <Skeleton variant="circular" width={64} height={64} />
      <div className="flex-1 space-y-2">
        <Skeleton width="60%" height={20} />
        <Skeleton width="40%" height={16} />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton width="100%" height={16} />
      <Skeleton width="80%" height={16} />
    </div>
  </div>
);

export const ListSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-theme-cards rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton variant="circular" width={48} height={48} />
          <div className="flex-1 space-y-2">
            <Skeleton width="60%" height={18} />
            <Skeleton width="40%" height={14} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const MessageSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-3 p-3 bg-theme-cards rounded-xl">
        <Skeleton variant="circular" width={48} height={48} />
        <div className="flex-1 space-y-2">
          <Skeleton width="50%" height={16} />
          <Skeleton width="80%" height={14} />
        </div>
        <Skeleton width={60} height={12} />
      </div>
    ))}
  </div>
);
